import { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import NotificationCard from '../notificationCard';
import CustomButton from '../customButton';
import ScopeGroups from './scopeGroups';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import { ReactComponent as ListIcon } from '../../../assets/icons/no-items.svg';
import { isArrayEmpty } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    width: 434,
    flexShrink: 0,
  },
  header: {
    borderBottom: `1px solid ${primary.bluish7}`,
    paddingBottom: spacing(4),
    marginBottom: spacing(6),
    width: '100%',
  },
  title: {
    marginBottom: spacing(1),
  },
  roleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  roleLabel: {
    lineHeight: '22px',
    userSelect: 'none',
  },
  roleLabelManagable: {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  noRole: {
    color: primary.bluish5,
  },
  addScopeButton: {
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish1}`,
    color: primary.bluish1,
    fontSize: 12,
    lineHeight: '14px',
    flexShrink: 0,
    marginLeft: spacing(2),
    padding: spacing(1, 2),
    minHeight: 22,
    maxHeight: 22,
    transition: 'backgroundColor .3s ease, border .3s ease, color .3s ease',
    '&:hover': {
      backgroundColor: primary.white,
    },
    '&:disabled': {
      backgroundColor: primary.white,
      border: `1px solid ${primary.bluish5}`,
      color: primary.bluish5,
    },
  },
  notificationCard: {
    padding: spacing(14.5, 10, 14, 10),
  },
}));

const UserRole = ({
  translations = {},
  role = '',
  scopeGroups = [],
  isManagable,
  hasAddScope = false,
  isScopeDisabled = false,
  canAccessUserProfileCheck,
  onGoToUserProfile,
  onManageRole,
  onAddScope = () => {},
  onEditScope = () => {},
  onDeleteScope = () => {},
}) => {
  const classes = useStyles();

  const hasGroups = !isArrayEmpty(scopeGroups);

  const roleName = useMemo(() => {
    if (!role) {
      if (isManagable) {
        return translations.addRole;
      }

      return translations.noRoleAssigned;
    }

    return role;
  }, [translations, role, isManagable]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title} variant="subtitle1">
          {translations.title}
        </Typography>
        <div className={classes.roleWrapper}>
          <TextBoxWithTooltip
            className={classNames(classes.roleLabel, {
              [classes.roleLabelManagable]: isManagable,
              [classes.noRole]: !role && !isManagable,
            })}
            variant="subtitle2"
            text={roleName}
            onClickHandler={onManageRole}
          />
          {hasAddScope && (
            <CustomButton
              className={classes.addScopeButton}
              type="rounded"
              disabled={isScopeDisabled}
              onClick={onAddScope}
            >
              {translations.addScopeButton}
            </CustomButton>
          )}
        </div>
      </div>
      <ScopeGroups
        translations={translations.menuItems}
        groups={scopeGroups}
        isManagable={isManagable}
        hasActions={hasAddScope && isManagable && !isScopeDisabled}
        canAccessUserProfileCheck={canAccessUserProfileCheck}
        onGoToUserProfile={onGoToUserProfile}
        onEditScope={onEditScope}
        onDeleteScope={onDeleteScope}
      />
      <NotificationCard
        className={classes.notificationCard}
        title={
          !role
            ? translations.noRoleResults.title
            : translations.noEmployeesResults.title
        }
        message={
          !role
            ? translations.noRoleResults.message
            : translations.noEmployeesResults.message
        }
        hasIcon
        customIcon={ListIcon}
        shouldFade={!hasGroups}
      />
    </div>
  );
};

UserRole.propTypes = {
  translations: PropTypes.object,
  role: PropTypes.string,
  scopeGroups: PropTypes.arrayOf(PropTypes.object),
  isManagable: PropTypes.bool.isRequired,
  hasAddScope: PropTypes.bool,
  isScopeDisabled: PropTypes.bool,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  onGoToUserProfile: PropTypes.func.isRequired,
  onManageRole: PropTypes.func.isRequired,
  onAddScope: PropTypes.func,
  onEditScope: PropTypes.func,
  onDeleteScope: PropTypes.func,
};

export default UserRole;
